import type {
  ChildId,
  ChildWithProfile,
  CollectionId,
  ImportCandidate,
  ImportJob,
  ImportJobItem,
  ImportSource,
  Language,
  RecentImportSource,
} from '@kidshub/contracts';
import { languageForLocale } from '@kidshub/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { formatClock, toMinutes } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { formatLastSeen } from '../lib/relative-time.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  Chip,
  Icon,
  type IconName,
  ProgressBar,
} from '../ui/kit.js';
import { type AudioLanguageChoices, audioLanguageChoices } from './audio-language.js';
import { type CandidateState, candidateState, isWorthImporting } from './import-candidates.js';
import './import.css';

/**
 * Bringing a channel into the library.
 *
 * Three things this screen refuses to do quietly, because each of them is the
 * difference between a tool and a trap:
 *
 * - **It shows what it found before it downloads anything.** Titles and lengths,
 *   with everything selected but every one of them unselectable. A parent
 *   pointing at a channel of two thousand videos should see the number before
 *   the disk does.
 * - **It says what it will cost.** Videos are files now, and a hundred of them
 *   is gigabytes on the machine running the server.
 * - **It keeps importing separate from approving.** The child chips are here for
 *   convenience, and leaving them empty is the safe answer: the videos land in
 *   the library and reach nobody until somebody says so.
 */

/** Roughly what a minute of 720p costs, measured on real downloads. */
const MEGABYTES_PER_MINUTE = 11;

/**
 * Past this, a "video" on a children's channel is nearly always a compilation:
 * an hour of episodes stitched together. Flagged rather than hidden, because it
 * is a fair choice for a long car journey and a poor one for a five-minute slot.
 */
const LONG_COMPILATION_SECONDS = 20 * 60;

/**
 * What another screen hands over when it recognised a channel link.
 *
 * The add-content page takes single videos only, and a channel pasted there
 * used to be turned away as "not a YouTube link" — true of a video, false of
 * what the parent was holding. It now sends the link here instead, and this
 * pair is the one place the shape of that handover is written down.
 */
interface ImportSourceHandover {
  url: string;
}

export const toImportSourceHandover = (url: string): ImportSourceHandover => ({ url });

const readImportSourceHandover = (state: unknown): ImportSourceHandover | null =>
  typeof state === 'object' && state !== null && 'url' in state && typeof state.url === 'string'
    ? { url: state.url }
    : null;

export interface FlowStep {
  label: string;
  /** Absent where going back would undo nothing useful, or cannot be undone. */
  onSelect?: () => void;
}

/**
 * Where a parent is in a short flow, and the way back.
 *
 * Shared by both ways content comes in, so that adding one video and adding a
 * channel read as the same kind of task. Earlier steps are buttons only when
 * returning to them is safe; the current one is announced, not pressable.
 */
export const FlowSteps = ({ steps, current }: { steps: FlowStep[]; current: number }) => {
  const { t } = useTranslation();

  return (
    <ol className="steps import-steps" aria-label={t('import.stepsLabel')}>
      {steps.map((step, index) => {
        const isDone = index < current;
        return (
          <li key={step.label}>
            <button
              type="button"
              className={isDone ? 'is-done' : undefined}
              aria-current={index === current ? 'step' : undefined}
              disabled={index === current || step.onSelect === undefined}
              onClick={step.onSelect}
            >
              <span className="n" aria-hidden="true">
                {isDone ? <Icon name="check" small /> : index + 1}
              </span>
              <span className="import-step-label">{step.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

type Stage =
  | { name: 'SOURCE' }
  | { name: 'CHOOSING'; source: ImportSource; candidates: ImportCandidate[] }
  | { name: 'RUNNING'; jobId: ImportJob['id']; candidates: ImportCandidate[] };

/** Which action an error belongs to, so it is shown beside the button that caused it. */
type Failure = { key: string; during: 'INSPECT' | 'START' };

export const ImportSourcePage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const handover = readImportSourceHandover(useLocation().state);

  const children = useResource('children', () => apiClient.children.list());
  const collections = useResource('collections', () => apiClient.collections.list());
  const recent = useResource('importSources', () => apiClient.imports.recentSources());

  const [stage, setStage] = useState<Stage>({ name: 'SOURCE' });
  const [url, setUrl] = useState(handover?.url ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hidesHeld, setHidesHeld] = useState(false);
  const [childIds, setChildIds] = useState<ChildId[]>([]);
  const [collectionId, setCollectionId] = useState<CollectionId | null>(null);
  // Null until the parent picks one: until then the children decide, and the
  // default follows them as they are ticked and unticked.
  const [chosenLanguage, setChosenLanguage] = useState<Language | null>(null);

  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];
  const languages = audioLanguageChoices(familyChildren, childIds);
  const audioLanguage =
    chosenLanguage !== null && languages.options.includes(chosenLanguage)
      ? chosenLanguage
      : languages.preferred;
  const [failure, setFailure] = useState<Failure | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const inspect = async (target: string) => {
    setFailure(null);
    setIsBusy(true);

    try {
      // Titles in the parent's own language, where the channel translated them.
      const { source, candidates } = await apiClient.imports.inspect({
        url: target,
        titleLanguage: languageForLocale(locale),
      });
      // Listing a source is what puts it on the recent list.
      recent.reload();
      /*
       * Nothing selected, and the parent chooses.
       *
       * Everything used to be, which read as helpful and is a trap: a channel
       * listing comes back two hundred videos long, and the button under it
       * then says "download 200 videos" — thirty-five gigabytes and an evening
       * of somebody's connection, one distracted tap away. "Select all" is one
       * click for a parent who means it.
       */
      setSelected(new Set());
      setStage({ name: 'CHOOSING', source, candidates });
    } catch (error) {
      setFailure({ key: toErrorKey(error), during: 'INSPECT' });
    } finally {
      setIsBusy(false);
    }
  };

  const start = async (source: ImportSource, candidates: ImportCandidate[]) => {
    setFailure(null);
    setIsBusy(true);

    try {
      const job = await apiClient.imports.start({
        sourceTitle: source.title,
        videos: candidates
          .filter((candidate) => selected.has(candidate.videoId))
          .map(({ videoId, title }) => ({ videoId, title })),
        ...(collectionId === null ? {} : { collectionId }),
        // Handed back unchanged; the backend gives it to the collection only
        // if that collection has no picture yet.
        sourcePosterUrl: source.posterUrl,
        childIds,
        audioLanguage,
      });
      setStage({ name: 'RUNNING', jobId: job.id, candidates });
    } catch (error) {
      setFailure({ key: toErrorKey(error), during: 'START' });
    } finally {
      setIsBusy(false);
    }
  };

  const toggle = (videoId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const backToSource = () => setStage({ name: 'SOURCE' });
  const stepIndex = stage.name === 'SOURCE' ? 0 : stage.name === 'CHOOSING' ? 1 : 2;

  return (
    <AppShell title={t('import.title')} subtitle={t('import.subtitle')}>
      <div className={stage.name === 'SOURCE' ? 'import-page' : 'import-page has-sum'}>
        <FlowSteps
          current={stepIndex}
          steps={[
            {
              label: t('import.steps.source'),
              ...(stage.name === 'CHOOSING' ? { onSelect: backToSource } : {}),
            },
            { label: t('import.steps.choose') },
            { label: t('import.steps.download') },
          ]}
        />

        <div className="import-layout">
          {stage.name === 'RUNNING' ? (
            <ImportProgress jobId={stage.jobId} candidates={stage.candidates} />
          ) : (
            <>
              <div className="import-main">
                <Card>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void inspect(url);
                    }}
                  >
                    <div className="field">
                      <label htmlFor="import-source-url">{t('import.sourceUrl')}</label>
                      <div className="url-row">
                        <input
                          id="import-source-url"
                          className="input"
                          type="text"
                          inputMode="url"
                          autoComplete="off"
                          spellCheck={false}
                          required
                          value={url}
                          onChange={(event) => setUrl(event.target.value)}
                          placeholder={t('import.channelUrlPlaceholder')}
                        />
                        <Button
                          type="submit"
                          variant={stage.name === 'SOURCE' ? 'primary' : 'secondary'}
                          disabled={isBusy}
                        >
                          {t(
                            isBusy && stage.name === 'SOURCE' ? 'import.looking' : 'import.inspect',
                          )}
                        </Button>
                      </div>
                      <p className="help">
                        {handover === null ? t('import.sourceUrlHelp') : t('import.handedOver')}
                      </p>
                      {failure?.during === 'INSPECT' ? (
                        <p className="help err" role="alert">
                          {t(failure.key)}
                        </p>
                      ) : null}
                    </div>
                  </form>
                </Card>

                {stage.name === 'SOURCE' && recent.resource.status === 'ready' ? (
                  <RecentSources
                    sources={recent.resource.data.sources}
                    isBusy={isBusy}
                    onOpen={(source) => {
                      setUrl(source.url);
                      void inspect(source.url);
                    }}
                    onForget={async (source) => {
                      recent.replace({
                        sources:
                          recent.resource.status === 'ready'
                            ? recent.resource.data.sources.filter((entry) => entry.id !== source.id)
                            : [],
                      });
                      try {
                        await apiClient.imports.forgetSource({ importSourceId: source.id });
                      } catch {
                        recent.reload();
                      }
                    }}
                  />
                ) : null}

                {stage.name === 'CHOOSING' ? (
                  <CandidateList
                    source={stage.source}
                    candidates={stage.candidates}
                    audioLanguage={audioLanguage}
                    selected={selected}
                    onToggle={toggle}
                    onSelect={(videoIds) => setSelected(new Set(videoIds))}
                    hidesHeld={hidesHeld}
                    onHidesHeld={setHidesHeld}
                  />
                ) : null}
              </div>

              <aside className="import-side">
                {stage.name === 'CHOOSING' ? (
                  <ImportOptions
                    candidates={stage.candidates}
                    selected={selected}
                    childIds={childIds}
                    onToggleChild={(childId) =>
                      setChildIds((previous) =>
                        previous.includes(childId)
                          ? previous.filter((id) => id !== childId)
                          : [...previous, childId],
                      )
                    }
                    familyChildren={familyChildren}
                    audioLanguage={audioLanguage}
                    languages={languages}
                    onAudioLanguage={setChosenLanguage}
                    allCollections={
                      collections.resource.status === 'ready'
                        ? collections.resource.data.collections
                        : []
                    }
                    collectionId={collectionId}
                    onCollection={setCollectionId}
                    isBusy={isBusy}
                    errorKey={failure?.during === 'START' ? failure.key : null}
                    onStart={() => void start(stage.source, stage.candidates)}
                  />
                ) : (
                  <WhatDownloads />
                )}
              </aside>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};

/**
 * The channels this family checked lately, to check again in one tap.
 *
 * Coming back to the same few channels for their new episodes is the usual
 * visit, and finding a channel's address on YouTube to paste it again was the
 * chore. Newest first; a parent can take one off the list.
 */
const RecentSources = ({
  sources,
  isBusy,
  onOpen,
  onForget,
}: {
  sources: RecentImportSource[];
  isBusy: boolean;
  onOpen: (source: RecentImportSource) => void;
  onForget: (source: RecentImportSource) => void;
}) => {
  const { t } = useTranslation();
  const locale = useLocale();

  if (sources.length === 0) {
    return null;
  }

  return (
    <Card title={t('import.recent.title')} meta={t('import.recent.hint')}>
      <ul className="import-recent">
        {sources.map((source) => (
          <li key={source.id}>
            <button
              type="button"
              className="import-recent-open"
              disabled={isBusy}
              onClick={() => onOpen(source)}
            >
              {source.posterUrl === null ? (
                <span className="import-recent-mark" aria-hidden="true">
                  <Icon name="play" small />
                </span>
              ) : (
                // The channel's own mark: a parent surface may load it.
                <img className="import-recent-mark" src={source.posterUrl} alt="" />
              )}
              <span className="import-recent-text">
                <b>{source.title}</b>
                <span>
                  {t('import.recent.lastChecked', {
                    when: formatLastSeen(source.lastInspectedAt, locale),
                  })}
                </span>
              </span>
              <Icon name="chev" small />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t('import.recent.forget', { title: source.title })}
              onClick={() => onForget(source)}
            >
              <Icon name="x" small />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

/** What importing means, said before the parent has pasted anything. */
const WhatDownloads = () => {
  const { t } = useTranslation();
  const facts: { icon: IconName; text: string }[] = [
    { icon: 'lock', text: t('import.factCopy') },
    { icon: 'down', text: t('import.factDisk', { megabytes: MEGABYTES_PER_MINUTE }) },
    { icon: 'x', text: t('import.factShorts') },
  ];

  return (
    <Card title={t('import.whatDownloads')} meta={t('import.whatDownloadsBody')}>
      <ul className="import-facts">
        {facts.map((fact) => (
          <li key={fact.icon}>
            <Icon name={fact.icon} small />
            <span>{fact.text}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

interface CandidateListProps {
  source: ImportSource;
  candidates: ImportCandidate[];
  audioLanguage: Language;
  selected: Set<string>;
  onToggle: (videoId: string) => void;
  onSelect: (videoIds: string[]) => void;
  hidesHeld: boolean;
  onHidesHeld: (hidesHeld: boolean) => void;
}

/**
 * The listing, with what the family already has marked.
 *
 * Coming back to a channel for its new episodes is the common visit, and a list
 * that cannot tell old from new is how the same episode gets downloaded twice.
 * Held videos stay selectable — re-importing is how a retitled or re-uploaded
 * one is refreshed — but "select all" leaves them out, and they can be hidden.
 */
const CandidateList = ({
  source,
  candidates,
  audioLanguage,
  selected,
  onToggle,
  onSelect,
  hidesHeld,
  onHidesHeld,
}: CandidateListProps) => {
  const { t } = useTranslation();

  const rows = candidates.map((candidate) => ({
    candidate,
    state: candidateState(candidate, audioLanguage),
  }));
  const worthIds = rows
    .filter((row) => isWorthImporting(row.state))
    .map((row) => row.candidate.videoId);
  const heldCount = rows.length - worthIds.length;
  const visible = hidesHeld ? rows.filter((row) => isWorthImporting(row.state)) : rows;

  return (
    <Card
      title={source.title}
      meta={t('import.found', {
        shown: candidates.length,
        ...(source.totalCount === null ? {} : { total: source.totalCount }),
      })}
      aside={
        source.posterUrl === null ? undefined : (
          // The channel's own mark, so the parent can see it is the right one.
          // A parent surface may load it; the tablet only ever gets a copy.
          <img className="import-poster" src={source.posterUrl} alt="" />
        )
      }
    >
      <div className="list-tools">
        <Button variant="ghost" small onClick={() => onSelect(worthIds)}>
          {heldCount === 0
            ? t('import.selectAll')
            : t('import.selectNew', { count: worthIds.length })}
        </Button>
        <Button variant="ghost" small onClick={() => onSelect([])}>
          {t('import.selectNone')}
        </Button>
        {heldCount === 0 ? null : (
          <Button variant="ghost" small onClick={() => onHidesHeld(!hidesHeld)}>
            {hidesHeld ? t('import.showHeld') : t('import.hideHeld', { count: heldCount })}
          </Button>
        )}
      </div>

      <div>
        {visible.map(({ candidate, state }) => {
          const isLong =
            candidate.durationSeconds !== null &&
            candidate.durationSeconds > LONG_COMPILATION_SECONDS;
          return (
            <label
              key={candidate.videoId}
              className={isWorthImporting(state) ? 'check-row' : 'check-row is-held'}
            >
              <input
                type="checkbox"
                className="cbx"
                checked={selected.has(candidate.videoId)}
                onChange={() => onToggle(candidate.videoId)}
              />
              <span>
                <span className="ttl">{candidate.title}</span>
                {isLong || state.kind !== 'NEW' ? (
                  <span className="flags">
                    <HeldBadge state={state} />
                    {isLong ? <Badge tone="amber">{t('import.longCompilation')}</Badge> : null}
                  </span>
                ) : null}
              </span>
              <span className="dur">
                {candidate.durationSeconds === null ? '—' : formatClock(candidate.durationSeconds)}
              </span>
            </label>
          );
        })}
      </div>
    </Card>
  );
};

const HeldBadge = ({ state }: { state: CandidateState }) => {
  const { t, i18n } = useTranslation();

  switch (state.kind) {
    case 'NEW':
      return null;
    case 'QUEUED':
      return <Badge tone="line">{t('import.queued')}</Badge>;
    case 'HELD':
      return <Badge tone="sage">{t('import.inLibrary')}</Badge>;
    case 'HELD_IN_OTHER':
      return (
        <Badge tone="line">
          {t('import.inLibraryIn', {
            languages: new Intl.ListFormat(i18n.language).format(
              state.languages.map((language) => t(`language.${language}`)),
            ),
          })}
        </Badge>
      );
  }
};

interface ImportOptionsProps {
  candidates: ImportCandidate[];
  selected: Set<string>;
  childIds: ChildId[];
  onToggleChild: (childId: ChildId) => void;
  familyChildren: ChildWithProfile[];
  audioLanguage: Language;
  languages: AudioLanguageChoices;
  onAudioLanguage: (language: Language) => void;
  allCollections: { id: CollectionId; title: string }[];
  collectionId: CollectionId | null;
  onCollection: (collectionId: CollectionId | null) => void;
  isBusy: boolean;
  errorKey: string | null;
  onStart: () => void;
}

const ImportOptions = ({
  candidates,
  selected,
  childIds,
  onToggleChild,
  familyChildren,
  audioLanguage,
  languages,
  onAudioLanguage,
  allCollections,
  collectionId,
  onCollection,
  isBusy,
  errorKey,
  onStart,
}: ImportOptionsProps) => {
  const { t } = useTranslation();

  const chosen = candidates.filter((candidate) => selected.has(candidate.videoId));
  const totalSeconds = chosen.reduce(
    (total, candidate) => total + (candidate.durationSeconds ?? 0),
    0,
  );
  const estimatedMegabytes = Math.round((totalSeconds / 60) * MEGABYTES_PER_MINUTE);

  return (
    <>
      <Card>
        <div className="opt-group">
          <h3>{t('addContent.collection')}</h3>
          <div className="chips">
            <Chip selected={collectionId === null} onToggle={() => onCollection(null)}>
              {t('addContent.noCollection')}
            </Chip>
            {allCollections.map((collection) => (
              <Chip
                key={collection.id}
                selected={collectionId === collection.id}
                onToggle={() => onCollection(collection.id)}
              >
                {collection.title}
              </Chip>
            ))}
          </div>
        </div>

        <div className="opt-group">
          <h3>{t('import.whoCanSee')}</h3>
          <div className="chips">
            {familyChildren.map((entry) => (
              <Chip
                key={entry.child.id}
                selected={childIds.includes(entry.child.id)}
                onToggle={() => onToggleChild(entry.child.id)}
              >
                {entry.child.name}
              </Chip>
            ))}
          </div>
          <p className="help">
            {childIds.length === 0 ? t('import.nobodyYet') : t('import.approvedOnArrival')}
          </p>
        </div>

        <div className="opt-group">
          <h3>{t('import.audioLanguage')}</h3>
          <div className="chips">
            {languages.options.map((language) => (
              <Chip
                key={language}
                selected={language === audioLanguage}
                onToggle={() => onAudioLanguage(language)}
              >
                {t(`language.${language}`)}
                {languages.natives.includes(language) ? (
                  <small>{t('import.nativeLanguage')}</small>
                ) : null}
              </Chip>
            ))}
          </div>
          <p className="help">{t('import.audioLanguageHelp')}</p>
        </div>
      </Card>

      {/* On a phone this card is pinned above the tab bar: the cost and the button stay in reach. */}
      <div className="sum card">
        <div className="sum-stats" aria-live="polite">
          <div>
            <b>{chosen.length}</b>
            <span>{t('import.sumVideos')}</span>
          </div>
          <div>
            <b>{t('import.approx', { value: toMinutes(totalSeconds) })}</b>
            <span>{t('import.sumMinutes')}</span>
          </div>
          <div>
            <b>{t('import.approx', { value: estimatedMegabytes })}</b>
            <span>{t('import.sumMegabytes')}</span>
          </div>
        </div>
        <Button
          variant="primary"
          block
          icon="down"
          disabled={isBusy || chosen.length === 0}
          onClick={onStart}
        >
          {chosen.length === 0
            ? t('import.chooseVideos')
            : t('import.start', { count: chosen.length })}
        </Button>
        {errorKey === null ? null : (
          <p className="help err" role="alert">
            {t(errorKey)}
          </p>
        )}
      </div>
    </>
  );
};

/** How often to ask. Downloads are minutes each; a slower poll would do. */
const PROGRESS_POLL_MS = 4000;

type ItemTone = 'done' | 'run' | 'wait' | 'miss';

const ITEM_ICONS: Record<ItemTone, IconName> = {
  done: 'check',
  run: 'down',
  wait: 'wait',
  miss: 'alert',
};

/**
 * How one video reads in the list.
 *
 * A job that has stopped leaves its unstarted videos marked pending; saying
 * "queued" about them then would be a promise nothing is going to keep.
 */
const describeItem = (
  item: ImportJobItem,
  isJobRunning: boolean,
): { tone: ItemTone; labelKey: string } => {
  switch (item.status) {
    case 'ADDED':
      return { tone: 'done', labelKey: 'import.item.ADDED' };
    case 'FAILED':
      return {
        tone: 'miss',
        labelKey: `import.failure.${item.failureCode ?? 'DOWNLOAD_FAILED'}`,
      };
    case 'DOWNLOADING':
    case 'PENDING':
      if (!isJobRunning) {
        return { tone: 'wait', labelKey: 'import.item.STOPPED' };
      }
      return item.status === 'DOWNLOADING'
        ? { tone: 'run', labelKey: 'import.item.DOWNLOADING' }
        : { tone: 'wait', labelKey: 'import.item.PENDING' };
  }
};

const ImportProgress = ({
  jobId,
  candidates,
}: {
  jobId: ImportJob['id'];
  candidates: ImportCandidate[];
}) => {
  const { t } = useTranslation();
  const [job, setJob] = useState<ImportJob | null>(null);
  const [items, setItems] = useState<ImportJobItem[]>([]);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const state = await apiClient.imports.get({ importJobId: jobId });
        if (!cancelled) {
          setJob(state.job);
          setItems(state.items);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorKey(toErrorKey(error));
        }
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), PROGRESS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [jobId]);

  const cancel = async () => {
    try {
      setJob(await apiClient.imports.cancel({ importJobId: jobId }));
    } catch (error) {
      setErrorKey(toErrorKey(error));
    }
  };

  const durations = new Map(
    candidates.map((candidate) => [candidate.videoId, candidate.durationSeconds]),
  );
  const isRunning = job?.status === 'RUNNING';
  const statusLine =
    job === null
      ? t('common.loading')
      : t(`import.status.${job.status}`, {
          completed: job.completed,
          total: job.total,
          failed: job.failed,
        });

  return (
    <>
      <div className="import-main">
        <Card title={job?.sourceTitle ?? t('common.loading')} meta={statusLine}>
          {job === null || job.total === 0 ? null : (
            <ProgressBar
              value={(job.completed + job.failed) / job.total}
              label={t('import.progressLabel')}
            />
          )}
          <p className="help import-keeps-going">{t('import.keepsGoing')}</p>
          {errorKey === null ? null : (
            <p className="help err" role="alert">
              {t(errorKey)}
            </p>
          )}

          <ul className="import-progress">
            {items.map((item) => {
              const { tone, labelKey } = describeItem(item, isRunning);
              const durationSeconds = durations.get(item.videoId) ?? null;
              return (
                <li key={item.videoId} className="prog-row">
                  <span className={`prog-ic ${tone}`}>
                    <Icon name={ITEM_ICONS[tone]} small />
                  </span>
                  <div>
                    <p className="prog-t">
                      {item.title}{' '}
                      {durationSeconds === null ? null : (
                        <span className="t-meta num">{formatClock(durationSeconds)}</span>
                      )}
                    </p>
                    <p className={`prog-s ${tone}`}>{t(labelKey)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <aside className="import-side">
        <div className="sum card">
          <div className="sum-stats" aria-live="polite">
            <div>
              <b>{t('import.ofTotal', { done: job?.completed ?? 0, total: job?.total ?? 0 })}</b>
              <span>{t('import.sumDownloaded')}</span>
            </div>
            <div>
              <b>{job?.failed ?? 0}</b>
              <span>{t('import.sumFailed')}</span>
            </div>
          </div>
          <div className="import-sum-actions">
            {isRunning ? (
              <Button variant="secondary" icon="stop" onClick={() => void cancel()}>
                {t('import.cancel')}
              </Button>
            ) : null}
            <ButtonLink variant="primary" to="/library">
              {t('import.goToLibrary')}
            </ButtonLink>
          </div>
        </div>
      </aside>
    </>
  );
};
